// client/src/components/OrderDetail.jsx
import React from "react";
import StatusBadge from "./StatusBadge";
import { useOrderDetail, useOrderHistory } from "../hooks/useOrders";
import { formatEasternDateTime } from "../utils/timezone";

const formatDate = (iso) =>
  formatEasternDateTime(iso, { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(parseFloat(amount || 0));

const formatHistorySignal = (signal) => {
  if (!signal) return null;
  const sku = signal.sku_or_product ? ` [${signal.sku_or_product}]` : "";
  return `${signal.type}: ${signal.from} -> ${signal.to}${sku}`;
};

const Section = ({ title, children }) => (
  <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9" }}>
    <p style={{
      margin: "0 0 14px", fontSize: 10, fontWeight: 800, color: "#94a3b8",
      letterSpacing: "0.1em", textTransform: "uppercase",
    }}>
      {title}
    </p>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
    <span style={{ fontSize: 13, color: "#64748b", flexShrink: 0, marginRight: 16 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", textAlign: "right" }}>{value || "—"}</span>
  </div>
);

const LocationBadge = ({ label }) => (
  <span style={{
    background: "#f0fdf4", color: "#166534",
    border: "1px solid #bbf7d0", borderRadius: 8,
    padding: "4px 12px", fontSize: 12,
    fontFamily: "monospace", fontWeight: 700,
    display: "inline-block",
  }}>
    📍 {label}
  </span>
);

const PanelShell = ({ onClose, children }) => (
  <>
    {/* Backdrop */}
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.35)",
        backdropFilter: "blur(2px)",
        zIndex: 99,
      }}
    />
    {/* Panel */}
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0,
      width: 440, background: "white",
      boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
      zIndex: 100, display: "flex", flexDirection: "column",
      overflowY: "auto",
      animation: "slideInPanel 0.25s cubic-bezier(0.34,1.56,0.64,1)",
    }}>
      <style>{`
        @keyframes slideInPanel {
          from { transform: translateX(60px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      {children}
    </div>
  </>
);

const OrderDetail = ({ orderId, onClose }) => {
  const { order, loading, error } = useOrderDetail(orderId);
  const { history, live, loading: historyLoading, error: historyError } = useOrderHistory(orderId);

  if (!orderId) return null;

  // Loading state
  if (loading) {
    return (
      <PanelShell onClose={onClose}>
        <div style={{
          padding: "20px 24px", background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)",
          color: "white", flexShrink: 0,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <p style={{ margin: "0 0 4px", fontSize: 11, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700 }}>
                Order Details
              </p>
              <div style={{ height: 28, width: 120, background: "rgba(255,255,255,0.15)", borderRadius: 6, marginBottom: 6 }} />
              <div style={{ height: 14, width: 160, background: "rgba(255,255,255,0.1)", borderRadius: 4 }} />
            </div>
            <button onClick={onClose} style={{
              background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8,
              width: 36, height: 36, cursor: "pointer", fontSize: 16, color: "white",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              ✕
            </button>
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 48 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 16, animation: "spin 1s linear infinite", display: "inline-block" }}>⟳</div>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>Loading order details from API…</p>
          </div>
        </div>
      </PanelShell>
    );
  }

  // Error state
  if (error) {
    return (
      <PanelShell onClose={onClose}>
        <div style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <p style={{ fontWeight: 700, color: "#b91c1c", marginBottom: 8 }}>Failed to load order</p>
          <p style={{ color: "#64748b", fontSize: 13 }}>{error}</p>
          <button onClick={onClose} style={{
            marginTop: 16, padding: "10px 24px", background: "#0f172a", color: "white",
            border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer",
          }}>
            Close
          </button>
        </div>
      </PanelShell>
    );
  }

  if (!order) return null;

  const initials = `${order.customer.first_name?.[0] || ""}${order.customer.last_name?.[0] || ""}` || "?";
  const orderFulfillmentStatus = String(order.fulfillment_status || "").toLowerCase();
  const isUnfulfilled = ["unfulfilled", "pending", "unshipped"].includes(orderFulfillmentStatus);
  const unfulfilledItems = order.line_items.filter((i) =>
    ["unfulfilled", "pending", "unshipped"].includes(String(i.fulfillment_status || "").toLowerCase())
  );

  return (
    <PanelShell onClose={onClose}>
      {/* Header */}
      <div style={{
        padding: "20px 24px", background: "linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)",
        color: "white", flexShrink: 0,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: 11, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700 }}>
              Order Details
            </p>
            <h2 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 900, fontFamily: "monospace" }}>
              {order.order_number}
            </h2>
            <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>{formatDate(order.created_at)}</p>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8,
            width: 36, height: 36, cursor: "pointer", fontSize: 16, color: "white",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            ✕
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <StatusBadge status={order.financial_status} size="lg" />
          <StatusBadge status={order.fulfillment_status} size="lg" />
        </div>
      </div>

      {/* Unfulfilled Location Alert */}
      {isUnfulfilled && (order.location_name || order.location_id) && (
        <div style={{
          margin: "16px 24px 0",
          background: "#fefce8", border: "1px solid #fde047", borderRadius: 10,
          padding: "12px 16px",
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#854d0e", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
            Allocated Location (Unfulfilled)
          </div>
          <LocationBadge label={order.location_name || order.location_id} />
        </div>
      )}

      {/* Customer */}
      <Section title="Customer">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 46, height: 46, borderRadius: "50%",
            background: "linear-gradient(135deg,#6366f1,#0ea5e9)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "white", fontWeight: 900, fontSize: 15, flexShrink: 0,
          }}>
            {initials}
          </div>
          <div>
            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 15 }}>{order.customer.full_name}</div>
            <div style={{ color: "#64748b", fontSize: 13 }}>{order.customer.email || "No email"}</div>
            {order.customer.phone && <div style={{ color: "#64748b", fontSize: 13 }}>{order.customer.phone}</div>}
            {order.customer.orders_count > 1 && (
              <div style={{ marginTop: 4, fontSize: 11, color: "#0ea5e9", fontWeight: 600 }}>
                🔁 {order.customer.orders_count} total orders
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* Products */}
      <Section title={`Products · ${order.total_quantity} item${order.total_quantity !== 1 ? "s" : ""}`}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {order.line_items.map((item, i) => (
            <div key={i} style={{
              background: "#f8fafc", borderRadius: 12, padding: "12px 14px",
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
              border: "1px solid #f1f5f9",
            }}>
              <div style={{ flex: 1, marginRight: 12 }}>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>{item.title}</div>
                {item.variant_title && (
                  <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{item.variant_title}</div>
                )}
                {item.sku && (
                  <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 1, fontFamily: "monospace" }}>
                    SKU: {item.sku}
                  </div>
                )}
                <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <StatusBadge status={item.fulfillment_status} />
                  {["unfulfilled", "pending", "unshipped"].includes(String(item.fulfillment_status || "").toLowerCase()) &&
                    (item.location_name || item.location_id) && (
                    <span style={{
                      background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0",
                      borderRadius: 5, padding: "2px 7px", fontSize: 10,
                      fontFamily: "monospace", fontWeight: 700,
                    }}>
                      📍 {item.location_name || item.location_id}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: 14 }}>{formatCurrency(item.total)}</div>
                <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>
                  {formatCurrency(item.price)} × {item.quantity}
                </div>
                {item.quantity_shipped != null && item.quantity_shipped !== item.quantity && (
                  <div style={{
                    color: item.quantity_shipped > 0 ? "#166534" : "#991b1b",
                    fontSize: 11, fontWeight: 700, marginTop: 2,
                  }}>
                    {item.quantity_shipped} shipped
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Financial Summary */}
      <Section title="Order Summary">
        <Row label="Subtotal" value={formatCurrency(order.subtotal_price)} />
        <Row label="Tax" value={formatCurrency(order.total_tax)} />
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginTop: 12, paddingTop: 12, borderTop: "2px solid #e2e8f0",
        }}>
          <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 15 }}>Total</span>
          <span style={{ fontWeight: 900, color: "#0f172a", fontSize: 20 }}>
            {formatCurrency(order.total_price)}
          </span>
        </div>
      </Section>

      {/* Location */}
      <Section title="Location & Shipping">
        {(order.location_name || order.location_id) ? (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Order Location ID
            </div>
            <LocationBadge label={order.location_name || order.location_id} />
          </div>
        ) : (
          <span style={{ color: "#94a3b8", fontSize: 13, display: "block", marginBottom: 12 }}>No location assigned</span>
        )}
        {order.shipping_address && (
          <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.7 }}>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Shipping Address
            </div>
            <div>{order.shipping_address.address1}</div>
            <div>{order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.zip}</div>
            <div>{order.shipping_address.country}</div>
          </div>
        )}
      </Section>

      {/* Unfulfilled Items Summary */}
      {isUnfulfilled && unfulfilledItems.length > 0 && (
        <Section title={`Unfulfilled Items · ${unfulfilledItems.length}`}>
          {unfulfilledItems.map((item, i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 12px", background: "#fef9c3", borderRadius: 8, marginBottom: 6,
              border: "1px solid #fde047",
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{item.title}</div>
                {item.variant_title && <div style={{ fontSize: 11, color: "#92400e" }}>{item.variant_title}</div>}
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Qty: {item.quantity}</div>
              </div>
              {(item.location_name || item.location_id) ? (
                <LocationBadge label={item.location_name || item.location_id} />
              ) : (order.location_name || order.location_id) ? (
                <LocationBadge label={order.location_name || order.location_id} />
              ) : (
                <span style={{ color: "#94a3b8", fontSize: 11 }}>No location</span>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* Fulfillments */}
      {order.fulfillments?.length > 0 && (
        <Section title="Fulfillments">
          {order.fulfillments.map((f, i) => (
            <div key={i} style={{ background: "#f0fdf4", borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
              <Row label="Status" value={<StatusBadge status={f.status} />} />
              {f.tracking_number && <Row label="Tracking #" value={f.tracking_number} />}
              <Row label="Date" value={formatDate(f.created_at)} />
            </div>
          ))}
        </Section>
      )}

      {/* Notes & Tags */}
      {(order.note || order.tags) && (
        <Section title="Notes & Tags">
          {order.note && <p style={{ margin: "0 0 10px", fontSize: 13, color: "#334155", background: "#fef9c3", padding: "10px 12px", borderRadius: 8 }}>{order.note}</p>}
          {order.tags && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {order.tags.split(",").map((tag) => (
                <span key={tag} style={{ background: "#f1f5f9", color: "#475569", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                  {tag.trim()}
                </span>
              ))}
            </div>
          )}
        </Section>
      )}

      <Section title={`ShipHero Order History${history?.length ? ` · ${history.length}` : ""}`}>
        {historyLoading && (
          <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>Loading history...</p>
        )}
        {!historyLoading && historyError && (
          <p style={{ margin: 0, color: "#b91c1c", fontSize: 13 }}>{historyError}</p>
        )}
        {!historyLoading && !historyError && (
          <>
            {live && (
              <div style={{ marginBottom: 10, fontSize: 12, color: "#334155" }}>
                <strong>Live status:</strong> {live.fulfillment_status || "unknown"}
              </div>
            )}
            {history.length === 0 ? (
              <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>No ShipHero history returned.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {history.slice(0, 40).map((entry) => (
                  <div key={entry.id} style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "8px 10px",
                    background: "#f8fafc",
                  }}>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                      {formatDate(entry.created_at)} · {entry.username || "System"}
                    </div>
                    <div style={{ fontSize: 12, color: "#0f172a", marginBottom: entry.signal ? 4 : 0 }}>
                      {entry.information}
                    </div>
                    {entry.signal && (
                      <div style={{ fontSize: 11, color: "#0f766e", fontWeight: 700 }}>
                        {formatHistorySignal(entry.signal)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Section>

      {/* API Source Footer */}
      <div style={{
        padding: "12px 24px", background: "#f8fafc",
        borderTop: "1px solid #e8ecf0", fontSize: 11, color: "#94a3b8",
        display: "flex", alignItems: "center", gap: 6,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
        Fetched from monitor DB + ShipHero live history · Order ID: {order.id}
      </div>
    </PanelShell>
  );
};

export default OrderDetail;
